import React from 'react';
import {observable, makeObservable, runInAction} from 'mobx';
import {observer} from 'mobx-react';
import store from '../../s';
import {checkout, klaviyo_identify} from '../../../api';
import * as checkoutUtil from '../../checkoutUtil';
import ModalSimple from '../../../global/modal-simple';
import {Portal} from '../../../util';
import {ICheckoutStep, ICheckoutMessageType, IShippingOption} from '../../i';
import {applyShippingOptionChange, createShippingOptionFlight} from './selectShippingOption';
import {ShipMethodRadioGroup, ShipMethodRadioGroupRedesign} from './shipMethodRadioGroup';
import {isCheckoutRedesign} from '../../ab-redesign';
import {billingChoiceMade} from '../payment/ab-redesign-billing';

declare var modalOpen: any;

const shippingOptionFlight = createShippingOptionFlight();

@observer
class ShippingInfoSpecial extends React.Component {
    constructor(props) {
        super(props);
    }

    render() {
        return (
            <div className="">
                {store.checkout.msg_shipping_special.trim() !== '' && (
                    <div style={{paddingTop: 8}}>
                        <div className="hidden-lg hidden-xl">
                            <hr />
                        </div>
                        <div className="ship-methods-header">
                            Shipping Alert
                            <span style={{paddingLeft: 4}}>
                                <i className="fas fa-exclamation-square" />
                            </span>
                        </div>
                        {store.checkout.msg_shipping_special}
                        <hr />
                    </div>
                )}
            </div>
        );
    }
}

@observer
class ShippingInfoText extends React.Component {
    @observable more: boolean = false;
    @observable unique_id: number = -1;

    constructor(props) {
        super(props);
        this.unique_id = Math.floor(Math.random() * 16777216);
        makeObservable(this);
    }

    render() {
        return (
            <div className="ship-info">
                <ModalSimple
                    id={`modal-shipping-general-${this.unique_id}`}
                    title={'Extra Shipping Information'}
                >
                    <div>
                        <p>
                            Please note, inclement weather or emergency conditions may affect the delivery of your order. Please visit{' '}
                            <a
                                href="https://www.fedex.com"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                FedEx
                            </a>{' '}
                            for notification of these extenuating circumstances.
                        </p>
                    </div>
                </ModalSimple>
                <div className="mt-2">
                    <p>Our products are custom-made to order in Connecticut. We'll send an email with a tracking number when your order ships.</p>
                    {store.checkout.isMyFrames && <p className="mt-2">Drop ship orders are shipped within 10-15 business days and larger store orders are shipped within 10-15 business days plus transit time from our facility in Connecticut.</p>}
                    <button
                        style={{paddingLeft: 4}}
                        className="text-btn"
                        onClick={() => {
                            modalOpen(`modal-shipping-general-${this.unique_id}`);
                        }}
                        type="button"
                        aria-label="Show extra shipping information"
                    >
                        <i
                            className="fas fa-question-square"
                            style={{color: '#595959'}}
                        />
                    </button>
                </div>
                <div>
                    <i className="fab fa-fedex fa-4x" />
                </div>
            </div>
        );
    }
}

interface IShippingInfoBackOrder {
    BackOrderDate: string;
}

@observer
class ShippingInfoBackOrder extends React.Component<IShippingInfoBackOrder> {
    @observable more: boolean = false;

    constructor(props) {
        super(props);
        makeObservable(this);
    }

    render() {
        return (
            <div className="ship-info">
                <div className="pad-top">
                    <div className="alert alert-info">This order includes backordered products. These products are expected to ship by {this.props.BackOrderDate}.</div>
                </div>
                <div className="pad-top">
                    <i className="fab fa-fedex fa-4x" />
                </div>
            </div>
        );
    }
}

@observer
class ShippingAddrEdit extends React.Component<{children?: React.ReactNode}> {
    @observable loading: boolean = false;
    @observable showAllOptions: boolean = false;
    @observable selectionPending: boolean = false;
    @observable queuedOption: IShippingOption | undefined = undefined;

    constructor(props) {
        super(props);
        this.handleOptionChange = this.handleOptionChange.bind(this);
        this.submit = this.submit.bind(this);
        this.loading = false;
        this.showAllOptions = false;
        this.selectionPending = false;
        this.queuedOption = undefined;
        checkoutUtil.checkDefaultShipping();
        makeObservable(this);
    }

    async componentDidMount() {
        if (store.checkout.isMyFrames && !store.checkout.myframes_isDropShip) {
            store.moveStep(ICheckoutStep.payment);
        }
    }

    handleOptionChange = async (o: IShippingOption) => {
        await applyShippingOptionChange(o, {
            isLocked: () => this.loading,
            flight: shippingOptionFlight,
            setBusy: busy => {
                this.selectionPending = busy;
            },
            getSelected: () => store.checkout.ship.shipMethod,
            getPrices: () => ({
                shipping: store.checkout.cart.pricing.shipping,
                production: store.checkout.cart.pricing.production,
            }),
            setSelected: (method, prices) => {
                store.checkout.ship.shipMethod = method as IShippingOption;
                store.checkout.cart.pricing.shipping = prices.shipping;
                store.checkout.cart.pricing.production = prices.production;
            },
            setQueued: queued => {
                this.queuedOption = queued;
            },
            batch: fn => {
                runInAction(fn);
            },
            checkout,
            identify: klaviyo_identify,
        });
    };

    // Redesign: once the shopper has confirmed "Same as shipping address", 'cc-ship-select' goes straight to Payment; before
    // that it stops on the Billing address card (the pre-tick there is client-side until confirmed).
    redesignSkipsBilling() {
        return isCheckoutRedesign(store.checkout.isMyFrames) && store.checkout.billing.addrSameAsShip && billingChoiceMade();
    }

    submit = async () => {
        this.loading = true;
        if (this.redesignSkipsBilling()) {
            // The shipping address may have been edited since billing was confirmed: the server copies ship -> billing on sync.
            await checkout('checkout-sync');
        }
        const success = await checkout('cc-ship-select');
        runInAction(() => {
            // approved ria
            this.loading = false;
        });
    };

    renderRedesign() {
        const busy = this.loading || this.selectionPending;
        const special = store.checkout.msg_shipping_special.trim();
        const continueLabel = this.redesignSkipsBilling() ? 'Continue to payment' : 'Continue to billing';

        return (
            <div className="ab-ship-options">
                <div className="ab-ship-lead">Choose your delivery speed</div>
                <div className="ab-ship-note">All frames are handcrafted to order in Connecticut. You'll receive a tracking number once your order ships.</div>

                {special !== '' && (
                    <div
                        className="ab-ship-alert"
                        role="note"
                    >
                        <i
                            className="fas fa-exclamation-triangle"
                            aria-hidden={true}
                        />
                        <span>{special}</span>
                    </div>
                )}
                {store.checkout.cart.items.map(x =>
                    x.messages
                        .filter(o => o.msgType === ICheckoutMessageType.backorder)
                        .map((o, i) => (
                            <div
                                className="alert alert-info"
                                key={`${x.id}-${i}`}
                            >
                                This order includes backordered products. {o.msg}
                            </div>
                        )),
                )}

                <fieldset className="ab-ship-list">
                    <ShipMethodRadioGroupRedesign
                        options={store.checkout.ship.shipOptions.slice(0, this.showAllOptions ? store.checkout.ship.shipOptions.length : 3)}
                        selectedKey={this.queuedOption != undefined ? this.queuedOption.key : store.checkout.ship.shipMethod != undefined ? store.checkout.ship.shipMethod.key : undefined}
                        onSelect={this.handleOptionChange}
                        disabled={this.loading}
                    />
                    {!this.showAllOptions && store.checkout.ship.shipOptions.length > 3 && (
                        <button
                            type="button"
                            className="ab-add-link"
                            onClick={() => (this.showAllOptions = true)}
                            aria-label="Show all shipping options"
                        >
                            Show faster options
                            <i
                                className="fas fa-chevron-down"
                                aria-hidden={true}
                            />
                        </button>
                    )}
                </fieldset>

                {this.props.children}

                <div className="ab-step-actions">
                    <div
                        aria-live="assertive"
                        aria-atomic="true"
                        className="sr-only"
                    >
                        {this.selectionPending ? 'Updating shipping options' : this.loading ? 'Updating payment options' : ''}
                    </div>
                    <button
                        type="button"
                        className="btn-checkout btn-checkout-yellow"
                        aria-label={continueLabel}
                        onClick={this.submit}
                        disabled={busy}
                        aria-busy={busy}
                    >
                        {busy ? 'Updating options' : continueLabel}
                        {busy && (
                            <span className="pad-left">
                                <i
                                    className="fas fa-circle-notch fa-spin fast-spin fa-checkout-button"
                                    aria-hidden={true}
                                ></i>
                            </span>
                        )}
                    </button>
                </div>
            </div>
        );
    }

    render() {
        if (store.checkout.cstep === ICheckoutStep.shipping) {
            return <div />;
        } else if (store.checkout.ship.shipOptions == undefined || store.checkout.ship.shipOptions.length < 1) {
            return (
                <div
                    className=""
                    style={{marginTop: 12, marginBottom: 12}}
                >
                    <div className="alert alert-danger form-error">
                        <div className="error-title">Invalid Shipping Address</div>
                        <span style={{paddingRight: 4}}>
                            <i className="fas fa-exclamation-triangle"></i>
                        </span>
                        No shipping methods available for this address. Please review your shipping address.
                    </div>
                </div>
            );
        } else if (store.checkout.cstep === ICheckoutStep.shipping_methods && isCheckoutRedesign(store.checkout.isMyFrames)) {
            return this.renderRedesign();
        } else if (store.checkout.cstep === ICheckoutStep.shipping_methods) {
            return (
                <div className="ship-options">
                    <div className="ship-method-container">
                        <div className="d-none d-md-block info-desktop">
                            <div className="info pad-left">
                                <div style={{marginTop: '-60px'}}>
                                    <ShippingInfoSpecial />
                                </div>
                                <ShippingInfoText />
                                {store.checkout.cart.items.map(x => x.messages.filter(o => o.msgType === ICheckoutMessageType.backorder).map(o => <div className="alert alert-info">This order includes backordered products. {o.msg}</div>))}
                            </div>
                        </div>

                        <div className="ship-methods">
                            <fieldset>
                                <ShipMethodRadioGroup
                                    options={store.checkout.ship.shipOptions.slice(0, this.showAllOptions ? store.checkout.ship.shipOptions.length : 3)}
                                    selectedKey={this.queuedOption != undefined ? this.queuedOption.key : store.checkout.ship.shipMethod != undefined ? store.checkout.ship.shipMethod.key : undefined}
                                    onSelect={this.handleOptionChange}
                                    disabled={this.loading}
                                />
                                {!this.showAllOptions && store.checkout.ship.shipOptions.length > 3 && (
                                    <button
                                        className="ship-method text-btn"
                                        onClick={() => (this.showAllOptions = true)}
                                        type="button"
                                        aria-label="Show all shipping options"
                                    >
                                        <div></div>
                                        <div
                                            className="ship-desc"
                                            style={{paddingLeft: 38}}
                                        >
                                            <div
                                                className="link"
                                                style={{textTransform: 'uppercase', fontSize: 14}}
                                                tabIndex={-1}
                                            >
                                                Show faster options
                                                <span className="pad-left">
                                                    <i
                                                        className="fas fa-caret-down"
                                                        aria-hidden={true}
                                                    />
                                                </span>
                                            </div>
                                        </div>
                                        <div className="price"></div>
                                    </button>
                                )}
                                {store.checkout.cstep === ICheckoutStep.shipping_methods && (
                                    <div className="row pad-top pad-bottom">
                                        <div className="col-12 text-left">
                                            <div
                                                aria-live="assertive"
                                                aria-atomic="true"
                                                className="sr-only"
                                            >
                                                {this.selectionPending ? 'Updating shipping options' : this.loading ? 'Updating payment options' : ''}
                                            </div>
                                            <button
                                                type="button"
                                                className={`btn-checkout btn-checkout-yellow${Portal?.PortalID === 3 ? ' btn-checkout-yellow-portal3' : ''}`}
                                                aria-label="Continue to Payment"
                                                onClick={this.submit}
                                                disabled={this.loading || this.selectionPending}
                                                aria-busy={this.loading || this.selectionPending}
                                            >
                                                {!(this.loading || this.selectionPending) ? (store.checkout.isMyFrames ? 'Continue to Place Order' : 'Continue to Payment') : 'Updating Options'}
                                                {(this.loading || this.selectionPending) && (
                                                    <span className="pad-left">
                                                        <i
                                                            className="fas fa-circle-notch fa-spin fast-spin fa-checkout-button"
                                                            aria-hidden={true}
                                                        ></i>
                                                    </span>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </fieldset>
                        </div>
                    </div>
                    <div className="row d-block d-md-none">
                        <div className="col-12 info">
                            <ShippingInfoSpecial />
                            <ShippingInfoText />
                            {store.checkout.cart.items.map(x => x.messages.filter(o => o.msgType === ICheckoutMessageType.backorder).map(o => <div className="alert alert-info">{o.msg}</div>))}
                        </div>
                    </div>
                </div>
            );
        }
        // } else {
        //     return <div className='ship-options-read gray d-block d-md-none'>
        //         <div className='row'>
        //             <div className='col-12'>
        //                 <div className='form-inline-label form-label-light'>Shipping Method</div>
        //                 {(!store.checkout.isMyFrames || store.checkout.myframes_isDropShip) && <div>{store.checkout.ship.shipMethod.desc} {util.currency(store.checkout.ship.shipMethod.price_production + store.checkout.ship.shipMethod.price_shipping)}</div>}
        //                 {(!store.checkout.isMyFrames || store.checkout.myframes_isDropShip) && <div className='black b'>Get it by {store.checkout.ship.shipMethod.dates}</div>}
        //                 {store.checkout.isMyFrames && !store.checkout.myframes_isDropShip && <div className='black b'>Fedex Ground or my pre-determined shipping method</div>}
        //             </div>
        //         </div>
        //     </div>;
        // }
    }
}

export default ShippingAddrEdit;
